import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { api } from '@/lib/api';
type Share = { session_id: string; phase: 'check' | 'later'; version: number };
export function EnglishSharingPanel({ owner }: { owner: string }) {
  const client = useQueryClient();
  const requestKeys = useRef(new Map<string, string>());
  const shares = useQuery({
    queryKey: ['english-sharing', owner],
    queryFn: () => api<{ limit: number; shares: Share[] }>('/study/english-learning/sharing'),
    retry: false,
  });
  const withdraw = useMutation({
    mutationFn: (share: Share) => {
      const fingerprint = JSON.stringify(share);
      let id = requestKeys.current.get(fingerprint);
      if (!id) {
        id = crypto.randomUUID();
        requestKeys.current.set(fingerprint, id);
      }
      return api('/study/english-learning/sharing/withdraw', {
        method: 'POST',
        body: {
          session_id: share.session_id,
          phase: share.phase,
          expected_version: share.version,
          request_id: id,
        },
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['english-sharing'] });
      await client.invalidateQueries({ queryKey: ['english-learning'] });
    },
    onError: () => {
      void shares.refetch();
    },
  });
  return (
    <section className="card-base my-5" aria-label="English educator sharing">
      <h2 className="text-xl font-extrabold text-ink">My shared English checks</h2>
      <p className="mt-2 text-sm text-ink-soft">
        Sharing a submitted check is optional. Withdraw access here even when Institution Study
        consent is no longer active. Your earlier practice and Tutor conversation stay private.
      </p>
      {shares.isLoading && (
        <p role="status" className="mt-3">
          Loading sharing choices…
        </p>
      )}
      {shares.isError && (
        <div role="alert" className="mt-3">
          <p>Sharing choices could not load.</p>
          <button className="btn-pill-secondary mt-3" onClick={() => void shares.refetch()}>
            Reload sharing choices
          </button>
        </div>
      )}
      {shares.data?.shares.length === 0 && (
        <p className="mt-3 text-sm text-slate2">No shared English checks are recorded.</p>
      )}
      <ul className="mt-4 space-y-3">
        {shares.data?.shares.map((share, i) => (
          <li
            key={`${share.session_id}:${share.phase}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-hairline p-4"
          >
            <span className="text-sm font-bold text-ink">
              Shared check {i + 1} · {share.phase === 'later' ? 'later check' : 'different text'}
            </span>
            <button
              className="btn-pill-secondary"
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate(share)}
            >
              Withdraw educator access
            </button>
          </li>
        ))}
      </ul>
      {shares.data && (
        <p className="mt-3 text-xs text-slate2">
          Up to {shares.data.limit} recent shared Session records. You can also withdraw from the
          original English Session.
        </p>
      )}
      {withdraw.isSuccess && (
        <p role="status" className="mt-3 text-sm font-bold text-ink">
          Educator access withdrawn.
        </p>
      )}
      {withdraw.error && (
        <p role="alert" className="mt-3 text-sm text-brand-coral">
          {withdraw.error.message} Reload before retrying a changed version.
        </p>
      )}
    </section>
  );
}
